import React from 'react';
import type { CollectButtonSource } from '@soundxyz/utils/analytics';
import type { Maybe } from '@soundxyz/utils/helpers';
import { MarketplaceButton } from '@components/basics/MarketplaceButton';
import { Button } from '@components/main-components/button/Button';
import { useAuth } from '@context/auth/base';
import { trackClickEvent } from '@lib/analytics';
import { DataTestId } from '@utils/test';
import { Collect, ConnectWallet } from './shared';

export function ForeverCollectButton({
  releaseId,
  sourceComponent,
  fullWidth,
  showDisabledCollect,
  onBuy,
}: {
  releaseId: string;
  sourceComponent: CollectButtonSource;
  fullWidth?: boolean;
  showDisabledCollect?: boolean;
  onBuy: () => void;
}) {
  const { isAuthenticatedWithAPI } = useAuth();

  if (!isAuthenticatedWithAPI) {
    return (
      <ConnectWallet
        label="Sign in to Collect"
        size="L"
        fullWidth={fullWidth}
        sourceComponent={sourceComponent}
      />
    );
  }

  if (!!showDisabledCollect) {
    return <Collect variant="fuchsia" disabled fullWidth={fullWidth} size="L" />;
  }

  return (
    <Collect
      size="L"
      fullWidth={fullWidth}
      variant="fuchsia"
      onClick={() => {
        trackClickEvent({
          event: 'Collect Now Clicked',
          properties: {
            associationId: releaseId,
            associationType: 'Release',
            sourceComponent,
          },
        });
        onBuy();
      }}
    />
  );
}

export function CollectButtonV3({
  marketPlaceUrl,
  releaseId,
  sourceComponent,
  fullWidth,
  showDisabledCollect,
  onBuy,
  isSaleOverOrUserHitLimit,
  showPurchaseSecondaryListings,
}: {
  releaseId: string;
  marketPlaceUrl: Maybe<string>;
  sourceComponent: CollectButtonSource;
  fullWidth?: boolean;
  showDisabledCollect?: boolean;
  onBuy: () => void;
  isSaleOverOrUserHitLimit: boolean;
  showPurchaseSecondaryListings: boolean;
}) {
  const { isAuthenticatedWithAPI } = useAuth();

  // this simply links out to sound market if there are no active listings
  // if we have secondary listings to show, we will allow collecting on reservoir directly
  if (isSaleOverOrUserHitLimit && !showPurchaseSecondaryListings) {
    if (!!marketPlaceUrl) {
      return (
        <MarketplaceButton
          label="View on Sound Market"
          link={marketPlaceUrl}
          fullWidth
          size="L"
          onClick={() => {
            trackClickEvent({
              event: 'Buy on Sound Market Clicked',
              properties: {
                associationId: releaseId,
                associationType: 'Release',
                sourceComponent,
              },
            });
          }}
        />
      );
    } else {
      return <Collect disabled fullWidth={fullWidth} soldOut size="L" />;
    }
  }

  if (!isAuthenticatedWithAPI) {
    return (
      <ConnectWallet
        label="Sign in to Collect"
        size="L"
        fullWidth={fullWidth}
        sourceComponent={sourceComponent}
      />
    );
  }

  if (!!showDisabledCollect && !showPurchaseSecondaryListings) {
    return <Collect variant="fuchsia" disabled fullWidth={fullWidth} size="L" />;
  }

  if (showPurchaseSecondaryListings) {
    return (
      <Button
        variant="fuchsia"
        fullWidth={fullWidth}
        size="L"
        label="Collect on Secondary Market"
        onClick={() => {
          trackClickEvent({
            event: 'Collect Now Clicked',
            properties: {
              associationId: releaseId,
              associationType: 'Release',
              sourceComponent,
            },
          });
          onBuy();
        }}
        data-testid={DataTestId.RELEASE_PAGE_COLLECT_SECONDARY_BUTTON}
      />
    );
  }

  return (
    <Collect
      size="L"
      fullWidth={fullWidth}
      variant="fuchsia"
      onClick={() => {
        trackClickEvent({
          event: 'Collect Now Clicked',
          properties: {
            associationId: releaseId,
            associationType: 'Release',
            sourceComponent,
          },
        });
        onBuy();
      }}
    />
  );
}
